<!--
__________________________________________________
Summary 
‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾
Vue component for displaying message box style alerts and text.

General usage of this component will not need to specify inline properties on the component. 
All use cases will involve directly invoking showMessage, confirmMessage and hideMessage functions. 

__________________________________________________
Properties
‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾
title -   optional, the title to be displayed in header bar of dialog.
          This parameter can be overwritten with corresponding parameters for showMessage and showConfirm methods.

__________________________________________________
Methods
‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾

showMessage(msg, title, btnText, callback)
Shows a message dialog with a single button
msg:          Message to be displayed
title:        Title to be displayed in header, defaults to 'Message' if no value provided 
btnText:      Text to be shown on close button, defaults to 'OK' if no value provided
callback:     optional, function to invoke when OK button is clicked

______________

showConfirm(params)
Shows a message dialog with up to 3 buttons, each of which can invoke a separate action. This method is useful
when user interaction is required before invoking one or more actions. For example, confirming record deletion.

params:
msg:            Message to be displayed
title:          Title to be displayed in header, defaults to 'Message' if no value provided 
btnText/btnOK:  Text to be shown on confirm button, defaults to 'OK' if no value provided
btnCancel:      Text to be shown on cancel button, defaults to 'Cancel' if no value provided
btnDoNothing:   optional, text to be shown on 3rd button, if no value provided, 3rd button does not appear
onConfirm:      optional, function to invoke when confirm button is clicked
onCancel:       optional, function to invoke when cancel button is clicked

______________

hideMessage()
Hides the dialog if it's currently showing - no parameters

__________________________________________________
Usage
‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾
Register the messageDialog component in the template entry-point of your application, including 'ref="messageDialog"': 
<message-dialog ref="messageDialog"></message-dialog>

From javascript within any Vue components, you can reference showMessage, confirmMessage
and hideMessage methods via the $root.$refs.messageDialog handle. Below are several examples:

  // show a message dialog
  $root.$refs.messageDialog.showMessage('Great Scott!<br>Think of the orphans!','Something to Ponder','Alrighty');

  // show a confirm message
  $root.$refs.messageDialog.showConfirm(
    {msg:'Do you think Zoolander is a good movie?',title:'A Quick Poll',
    btnOk:'Yes, of course',btnCancel:'No',
    btnDoNothing: 'Close with no action',
    onConfirm:()=>{$root.$refs.statusMessage.showStatus('<b>You</b> are a distinguished individual!',null,null,null,null,'success')},
    onCancel:()=>{$root.$refs.statusMessage.showStatus('Why don`t you have a sense of humor?',null,null,null,null,'danger')}
  });

  // force close the message dialog without waiting for user interaction
  $root.$refs.messageDialog.hideMessage();

-->

<template>
  <div id="divAlert" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="myModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title" id="myModalLabel" v-html="alertTitle"></h4>
          <button type="button" class="close" data-dismiss="modal" v-on:click="processClose()"aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <span id="lblAlert" v-html="alertMessage"></span>
        </div>
        <div class="modal-footer">
          <button v-show="buttonNoAction!=''" type="button" class="btn btn-light" data-dismiss="modal">{{buttonNoAction}}</button>

          <template v-if="showCancel">
            <button v-show="showCancel" v-on:click="function(){if (onCancel) onCancel();}" type="button" class="btn btn-light"
              data-dismiss="modal">{{buttonCancel}}</button>
            <button v-show="showCancel" v-on:click="function(){if (onConfirm) onConfirm();}" type="button" class="btn btn-primary"
              data-dismiss="modal">{{buttonText}}</button>
          </template>

          <button v-show="!showCancel" type="button" 
            class="btn btn-primary" 
            v-on:click="function(){if (onConfirm) onConfirm();}"
            data-dismiss="modal">{{buttonText}}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'

export default {
  props: {
    title: {
      type: String,
      default: ""
    }
  },
  computed: {
    ...mapState({
      messageOptions: state => state.status.messageOptions,
      confirmOptions: state => state.status.confirmOptions,
    }),    
  },

  data: function () {
    return {
      alertMessage : '',
      isStatic: false,
      onConfirm : function() {},
      onCancel : function() {},
      showCancel : false,
      alertTitle : 'Message',
      buttonText : 'OK',
      buttonCancel : 'Cancel',
      buttonNoAction : ''
    }
  },
  watch: {
    messageOptions: function(options){
      if (options){
        this.showMessage(options);
      }
    },
    confirmOptions: function(options){
      if (options){
        this.showConfirm(options);
      }
    }
  },
  mounted(){
    // listen for invocations on event bus
    this.$eventBus.$on("showMessage", (params) => {
      this.showMessage(params);
    });
    this.$eventBus.$on("showConfirm", (params) => {
      this.showConfirm(params);
    });
  },
  methods: {
    showMessage: function(options) {
      let msg = (options.msg) ? options.msg : options;
      this.alertMessage = msg;
      this.alertTitle = (options.title!=undefined) ? options.title : (this.title ? this.title : 'Message');
      this.buttonText = (options.btnText || 'OK');
      this.buttonNoAction = '';
      this.showCancel = false;
      this.isStatic = options.isStatic || false;
      this.onConfirm = (options.callback) ? options.callback : null;
      this.onCancel = function() {};

      if (this.isStatic) {
        $('#divAlert').modal({
          backdrop: 'static',
          keyboard: false,
          show: true
        });
      } else {
        $('#divAlert').modal({
          backdrop: true,
          keyboard: true,
          show: true
        });
      }

      //$('#divAlert').modal('show');
    },
    showConfirm: function(options) {
      this.alertMessage = options.msg;
      this.alertTitle = (options.title!=undefined) ? options.title : (this.title ? this.title : 'Message');
      this.buttonText = options.btnOk || options.btnText || 'OK';
      this.buttonCancel = options.btnCancel || 'Cancel';
      this.buttonNoAction = options.btnDoNothing || '';
      this.showCancel = true;
      this.isStatic = options.isStatic || !(options.btnDoNothing);
      this.onConfirm = options.onConfirm;
      this.onCancel = options.onCancel;

      if (this.isStatic) {
        $('#divAlert').modal({
          backdrop: 'static',
          keyboard: false
        });
      } else {
        $('#divAlert').modal({
          backdrop: true,
          keyboard: true
        });
      }
      
      $('#divAlert').modal('show');
    },
    processClose: function() {
      if ((this.onCancel) && this.showCancel) {
        this.onCancel();
      } else if ((this.onConfirm) && !this.showCancel) {
        this.onConfirm();
      } 
    },

    hideMessage: function() {
      $('#divAlert').modal('hide');
    }

  },
}
</script>
